/**
 * PublicProposalPage — patient-facing treatment proposal page (Task #81,
 * Phase 2c, Jun 10 2026).
 *
 * Carlos's spec section §2 + §3 — the proposal page the patient lands
 * on after clicking the magic link their PEM emailed/SMSed. Lives
 * OUTSIDE the authenticated app shell (no sidebar, no IdleWarning, no
 * role-aware landing). Just a clean, branded ZignaGenix proposal.
 *
 * Endpoints consumed (Phase 2b — routes/treatment_proposals_public.js):
 *   POST /api/public/proposal/by-token  { token }
 *   POST /api/public/proposal/select    { token, selected_option_id, selected_addon_ids[] }
 *
 * Mobile-first: bottom-anchored CTA, 44px+ touch targets, sticky
 * running-total bar, single-column layout on narrow viewports. The
 * patient is almost certainly opening this on their phone via SMS.
 *
 * Status code handling matches Phase 2b's HTTP conventions:
 *   404 -> "link not found / malformed"
 *   410 -> "link expired or revoked"
 *   409 -> "proposal already finalized"
 *   200 -> render the proposal
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Sparkles, Check, AlertTriangle, Loader2, CheckCircle2,
  Phone, Star, Plane, Shield, FileText, Download, DollarSign, Video,
} from "lucide-react";

const ACCENT = "#2E8B98";
const ACCENT_DARK = "#1F6A75";
const RECOMMENDED = "#F59E0B";

export default function PublicProposalPage({ token }) {
  const [loadState, setLoadState] = useState("loading"); // loading | loaded | signing | error | saved
  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [data, setData] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Task #99 — Phase 4e — e-signature state (patient signing UI).
  // Acknowledgment set: Map<document_id, boolean>. Patient must ack EVERY
  // required document before the Sign button enables.
  const [docAcknowledgments, setDocAcknowledgments] = useState({});
  const [typedName, setTypedName] = useState("");
  const [esignConsent, setEsignConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showEsignText, setShowEsignText] = useState(false);

  // ─── Initial load ──────────────────────────────────────────────────
  const loadProposal = useCallback(async () => {
    if (!token) {
      setLoadState("error");
      setErrorCode("MISSING_TOKEN");
      return;
    }
    setLoadState("loading");
    try {
      const r = await fetch("/api/public/proposal/by-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) {
        let code = null;
        try { code = (await r.json()).code; } catch { /* ignore */ }
        setErrorCode(code || `HTTP_${r.status}`);
        setLoadState("error");
        return;
      }
      const payload = await r.json();
      setData(payload);
      // Initialize selection state from server payload OR fall back to
      // (recommended option + default-selected addons)
      if (payload.selection) {
        setSelectedOptionId(payload.selection.selected_option_id);
        setSelectedAddonIds(new Set(payload.selection.selected_addon_ids || []));
      } else {
        const rec = (payload.options || []).find(o => o.is_recommended);
        if (rec) setSelectedOptionId(rec.id);
        const defaultAddons = (payload.addons || [])
          .filter(a => a.default_selected)
          .map(a => a.id);
        setSelectedAddonIds(new Set(defaultAddons));
      }
      setLoadState("loaded");
    } catch (e) {
      setErrorCode("NETWORK");
      setErrorMessage(e.message);
      setLoadState("error");
    }
  }, [token]);

  useEffect(() => { loadProposal(); }, [loadProposal]);

  // ─── Running total (client-side preview; server computes authoritative) ──
  const runningTotal = useMemo(() => {
    if (!data) return { subtotal: 0, deposit: 0 };
    const opt = (data.options || []).find(o => o.id === selectedOptionId);
    const optPrice = opt ? Number(opt.price_cents) || 0 : 0;
    const addonsPrice = (data.addons || [])
      .filter(a => selectedAddonIds.has(a.id))
      .reduce((sum, a) => sum + (Number(a.price_cents) || 0), 0);
    const subtotal = optPrice + addonsPrice;
    return { subtotal, deposit: Math.round(subtotal * 0.5) };
  }, [data, selectedOptionId, selectedAddonIds]);

  // ─── Save handler ──────────────────────────────────────────────────
  async function save() {
    if (saving || !selectedOptionId) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      const r = await fetch("/api/public/proposal/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          selected_option_id: selectedOptionId,
          selected_addon_ids: Array.from(selectedAddonIds),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMessage(friendlyErrorFor(body.code || `HTTP_${r.status}`));
        setSaving(false);
        return;
      }
      // Task #99 — Phase 4e: transition to signing UI instead of done.
      setLoadState("signing");
    } catch (e) {
      setErrorMessage(`Network error: ${e.message}`);
    } finally { setSaving(false); }
  }

  // ─── Sign handler (Task #99 — Phase 4e) ─────────────────────────────────
  async function signNow() {
    if (signing) return;
    if (!typedName.trim() || !esignConsent) return;
    const requiredDocs = (data?.documents || []);
    if (requiredDocs.length === 0) {
      setErrorMessage("No documents found for this proposal. Contact your PEM.");
      return;
    }
    // Confirm every required document is acknowledged
    const missing = requiredDocs.filter(d => !docAcknowledgments[d.id]);
    if (missing.length > 0) {
      setErrorMessage(`Please review and acknowledge every document before signing.`);
      return;
    }

    setSigning(true);
    setErrorMessage(null);
    try {
      const r = await fetch("/api/public/proposal/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          typed_name: typedName.trim(),
          consent_acknowledged: true,
          document_signatures: requiredDocs.map(d => ({
            document_id: d.id,
            acknowledged: true,
          })),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMessage(friendlyErrorFor(body.code || `HTTP_${r.status}`));
        setSigning(false);
        return;
      }
      // Signing succeeded — transition to the post-sign confirmation view.
      setLoadState("saved");
    } catch (e) {
      setErrorMessage(`Network error: ${e.message}`);
    } finally { setSigning(false); }
  }

  // ─── Render: loading / error / saved / signing / loaded ────────────
  if (loadState === "loading") {
    return <FullPageState>
      <Loader2 size={32} className="spin" color={ACCENT} />
      <div style={{ marginTop: 12, fontSize: 14, color: "#6C757D" }}>Loading your treatment proposal…</div>
    </FullPageState>;
  }

  if (loadState === "error") {
    return <ErrorView code={errorCode} message={errorMessage} />;
  }

  if (loadState === "saved") {
    return <SavedView />;
  }

  if (loadState === "signing") {
    return <SigningView
      data={data}
      docAcknowledgments={docAcknowledgments}
      setDocAcknowledgments={setDocAcknowledgments}
      typedName={typedName}
      setTypedName={setTypedName}
      esignConsent={esignConsent}
      setEsignConsent={setEsignConsent}
      showEsignText={showEsignText}
      setShowEsignText={setShowEsignText}
      signing={signing}
      onSign={signNow}
      errorMessage={errorMessage}
    />;
  }

  // loaded
  const { proposal, options, addons, documents } = data;
  const recommendedId = (options || []).find(o => o.is_recommended)?.id;

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", paddingBottom: 120 }}>

      {/* ─── Header ─── */}
      <header style={{
        background: ACCENT, color: "white",
        padding: "20px 16px", textAlign: "center",
      }}>
        <div style={{ fontSize: 14, opacity: 0.85, letterSpacing: 1, fontWeight: 600 }}>ZIGNAGENIX</div>
        <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700 }}>Your Treatment Proposal</h1>
        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
          Prepared {proposal.ready_for_patient_at ? new Date(proposal.ready_for_patient_at).toLocaleDateString() : "—"}
          {proposal.expires_at && (
            <> &middot; Valid through {new Date(proposal.expires_at).toLocaleDateString()}</>
          )}
        </div>
      </header>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "16px" }}>

        {/* Physician notes — Carlos's spec: shown alongside options */}
        {proposal.physician_notes && (
          <div style={{
            background: "white", borderRadius: 12, padding: 16, marginBottom: 16,
            border: `1px solid #E9ECEF`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", letterSpacing: 0.5, marginBottom: 6 }}>
              <FileText size={12} style={{ verticalAlign: "middle" }} /> NOTE FROM YOUR PHYSICIAN
            </div>
            <div style={{ fontSize: 14, color: "#212529", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {proposal.physician_notes}
            </div>
          </div>
        )}

        {/* ─── Recommended Treatment Options ─── */}
        <Section title="Recommended Treatment Options" icon={Sparkles}>
          {(options || []).length === 0 ? (
            <div style={{ color: "#ADB5BD", fontSize: 13, padding: 12 }}>
              No options available. Contact your ZignaGenix PEM.
            </div>
          ) : (options || []).map(o => {
            const isSelected = selectedOptionId === o.id;
            const isRecommended = o.is_recommended;
            return (
              <button
                key={o.id}
                onClick={() => setSelectedOptionId(o.id)}
                style={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  background: "white",
                  border: `2px solid ${isSelected ? ACCENT : (isRecommended ? RECOMMENDED : "#E9ECEF")}`,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 10,
                  cursor: "pointer",
                  transition: "border-color .15s, transform .1s",
                  position: "relative",
                  minHeight: 80,
                }}
              >
                {isRecommended && (
                  <div style={{
                    position: "absolute", top: -10, left: 14,
                    background: RECOMMENDED, color: "white",
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    padding: "3px 10px", borderRadius: 999,
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}>
                    <Star size={10} fill="white" /> RECOMMENDED
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  {/* Custom radio */}
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%",
                    border: `2px solid ${isSelected ? ACCENT : "#CED4DA"}`,
                    background: isSelected ? ACCENT : "white",
                    flexShrink: 0, marginTop: 2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isSelected && <Check size={14} color="white" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#212529" }}>
                      {o.label}
                    </div>
                    <div style={{ fontSize: 14, color: "#212529", marginTop: 2 }}>
                      {prettyModality(o.modality)} &middot; {o.dose_description}
                    </div>
                    {o.biologic_source && (
                      <div style={{ fontSize: 12, color: "#6C757D", marginTop: 2 }}>{o.biologic_source}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: ACCENT_DARK }}>
                      ${(o.price_cents / 100).toLocaleString()}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </Section>

        {/* ─── Add-on marketplace ─── */}
        {(addons || []).length > 0 && (
          <Section title="Recovery & Longevity Enhancements" icon={Sparkles}>
            <div style={{ fontSize: 12, color: "#6C757D", marginBottom: 8 }}>
              Optional add-ons. Check the boxes for any you'd like included.
            </div>
            {addons.map(a => {
              const isOn = selectedAddonIds.has(a.id);
              return (
                <button
                  key={a.id}
                  onClick={() => {
                    setSelectedAddonIds(prev => {
                      const next = new Set(prev);
                      if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                      return next;
                    });
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    background: "white",
                    border: `1px solid ${isOn ? ACCENT : "#E9ECEF"}`,
                    borderRadius: 10, padding: 12, marginBottom: 6,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 12,
                    minHeight: 60,
                  }}
                >
                  {/* Custom checkbox */}
                  <div style={{
                    width: 22, height: 22, borderRadius: 5,
                    border: `2px solid ${isOn ? ACCENT : "#CED4DA"}`,
                    background: isOn ? ACCENT : "white",
                    flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isOn && <Check size={14} color="white" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#212529" }}>{a.name}</div>
                    {a.description && (
                      <div style={{ fontSize: 11, color: "#6C757D", marginTop: 2 }}>{a.description}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT_DARK, flexShrink: 0 }}>
                    ${(a.price_cents / 100).toLocaleString()}
                    {a.is_recurring && (
                      <div style={{ fontSize: 10, color: "#6C757D", fontWeight: 400 }}>/{a.billing_period || "month"}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </Section>
        )}

        {/* ─── Travel reminder ─── */}
        {proposal.travel_packet_required && (
          <div style={{
            background: "#FEF3C7", borderRadius: 10, padding: 12, marginBottom: 16,
            display: "flex", alignItems: "flex-start", gap: 10,
            fontSize: 13, color: "#92400E",
          }}>
            <Plane size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              Travel coordination is part of this proposal. Your PEM will share
              the travel packet (driver, hotel, transportation) after your selection.
            </div>
          </div>
        )}

        {/* ─── Documents (Phase 3, Task #88/#93) ─── */}
        {/* Each document is opened in a new tab so the patient stays on
            the proposal page. URLs are presigned with a 5-min TTL — if
            they expire before the patient clicks, the page reload
            re-fetches fresh ones from /api/public/proposal/by-token.
            Re-landed in Task #93 after the workspaceTabs bundle fix
            (Task #94) cleared the webpack 5 dual-tag error. */}
        {Array.isArray(documents) && documents.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{
              fontSize: 14, fontWeight: 700, color: "#1F2937",
              margin: "0 0 8px 0", display: "flex", alignItems: "center", gap: 6,
            }}>
              <Shield size={14} color={ACCENT} /> Your treatment documents
            </h3>
            <p style={{ fontSize: 12, color: "#6B7280", margin: "0 0 12px 0" }}>
              Review each of these before continuing. You will be asked to
              sign acknowledgments after your selection is finalized.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {documents.map(doc => {
                const Icon = doc.document_category === "informed_consent" ? Shield
                  : doc.document_category === "financial_terms" ? DollarSign
                  : doc.document_category === "telehealth_consent" ? Video
                  : doc.document_category === "travel_packet" ? Plane
                  : FileText;
                const sizeKb = doc.size_bytes ? (doc.size_bytes / 1024).toFixed(0) : "?";
                const disabled = !doc.download_url;
                return (
                  <a
                    key={doc.id}
                    href={doc.download_url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { if (disabled) e.preventDefault(); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: 12, background: "white",
                      border: "1px solid #E5E7EB", borderRadius: 10,
                      textDecoration: "none", color: "inherit",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      minHeight: 44,  // mobile touch target
                    }}
                  >
                    <div style={{
                      flexShrink: 0,
                      width: 36, height: 36, borderRadius: 8,
                      background: `${ACCENT}15`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon size={18} color={ACCENT} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: "#111827",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {doc.title || doc.document_category}
                      </div>
                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                        PDF · {sizeKb} KB
                        {doc.signed_at ? " · Signed" : doc.finalized_at ? " · Finalized" : " · Draft"}
                      </div>
                    </div>
                    <Download size={16} color={disabled ? "#9CA3AF" : ACCENT} style={{ flexShrink: 0 }} />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Inline error */}
        {errorMessage && (
          <div style={{
            background: "#FEE2E2", color: "#991B1B", padding: 12, borderRadius: 8,
            display: "flex", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 13,
          }}>
            <AlertTriangle size={16} /> {errorMessage}
          </div>
        )}
      </div>

      {/* ─── Sticky running total + Save button ─── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "white", borderTop: "1px solid #DEE2E6",
        padding: "12px 16px", boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
        zIndex: 100,
      }}>
        <div style={{ maxWidth: 640, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#6C757D", textTransform: "uppercase", letterSpacing: 0.5 }}>Total</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#212529", lineHeight: 1 }}>
              ${(runningTotal.subtotal / 100).toLocaleString()}
            </div>
            <div style={{ fontSize: 11, color: "#6C757D" }}>
              50% deposit: <strong>${(runningTotal.deposit / 100).toLocaleString()}</strong>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving || !selectedOptionId}
            style={{
              background: !selectedOptionId ? "#CED4DA" : ACCENT,
              color: "white",
              border: "none",
              padding: "14px 22px",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              cursor: !selectedOptionId ? "not-allowed" : "pointer",
              minHeight: 52,
              flexShrink: 0,
              minWidth: 160,
            }}
          >
            {saving ? <Loader2 size={16} className="spin" style={{ verticalAlign: "middle" }} />
                    : "Save my selection"}
          </button>
        </div>
      </div>

      {/* spin keyframes */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: "#6C757D", textTransform: "uppercase",
        letterSpacing: 0.5, marginBottom: 10, paddingLeft: 4,
        display: "flex", alignItems: "center", gap: 6,
      }}>
        {Icon && <Icon size={13} />} {title}
      </div>
      {children}
    </div>
  );
}

function FullPageState({ children }) {
  return (
    <div style={{
      background: "#F8F9FA", minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      {children}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

function ErrorView({ code, message }) {
  const friendly = friendlyErrorFor(code);
  return (
    <div style={{
      background: "#F8F9FA", minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24, textAlign: "center",
    }}>
      <div style={{
        background: "white", borderRadius: 12, padding: 32, maxWidth: 420,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}>
        <AlertTriangle size={48} color="#F26522" />
        <div style={{ fontSize: 20, fontWeight: 700, color: "#212529", marginTop: 14 }}>
          {friendly.title}
        </div>
        <div style={{ fontSize: 14, color: "#6C757D", marginTop: 8, lineHeight: 1.5 }}>
          {friendly.body}
        </div>
        {message && (
          <div style={{ fontSize: 11, color: "#ADB5BD", marginTop: 12, fontFamily: "monospace" }}>
            {message}
          </div>
        )}
        <div style={{ marginTop: 20, fontSize: 13, color: "#212529" }}>
          Contact your <strong>ZignaGenix Patient Experience Manager</strong> for a new link.
        </div>
        <a href="tel:+16197759646" style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          marginTop: 12, color: ACCENT, fontWeight: 600, fontSize: 14, textDecoration: "none",
        }}>
          <Phone size={14} /> Call ZignaGenix
        </a>
      </div>
    </div>
  );
}

function SavedView() {
  return (
    <div style={{
      background: "#F8F9FA", minHeight: "100vh",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24, textAlign: "center",
    }}>
      <div style={{
        background: "white", borderRadius: 12, padding: 36, maxWidth: 440,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}>
        <CheckCircle2 size={56} color="#62BB46" />
        <div style={{ fontSize: 22, fontWeight: 700, color: "#212529", marginTop: 16 }}>
          Signed — awaiting physician
        </div>
        <div style={{ fontSize: 14, color: "#495057", marginTop: 12, lineHeight: 1.6 }}>
          Thank you. Your signature has been recorded for every document.
          Your prescribing physician will counter-sign within 24 hours and
          a ZignaGenix Patient Experience Manager will reach out to schedule
          your treatment date and confirm payment.
        </div>
        <div style={{
          marginTop: 20, padding: 12, background: "#F0F4F8",
          borderRadius: 8, fontSize: 12, color: "#6C757D",
        }}>
          <Shield size={14} style={{ verticalAlign: "middle" }} /> Your information is protected under HIPAA + Mexico LFPDPPP. Signature recorded per NOM-151 + ESIGN.
        </div>
      </div>
    </div>
  );
}

// ─── SigningView (Task #99 — Phase 4e) ───────────────────────────────────
// Patient signing surface. Shown after the patient clicks Save on their
// selection. Lists every required document with an acknowledgment checkbox,
// shows the ESIGN consent disclosure (collapsible), captures the typed
// name, and submits to POST /api/public/proposal/sign.
function SigningView({
  data, docAcknowledgments, setDocAcknowledgments,
  typedName, setTypedName,
  esignConsent, setEsignConsent,
  showEsignText, setShowEsignText,
  signing, onSign, errorMessage,
}) {
  const documents = (data && data.documents) || [];
  const allAcknowledged = documents.length > 0 && documents.every(d => docAcknowledgments[d.id]);
  const canSign = allAcknowledged && esignConsent && typedName.trim().length > 0 && !signing;

  function toggleAck(docId) {
    setDocAcknowledgments({ ...docAcknowledgments, [docId]: !docAcknowledgments[docId] });
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", paddingBottom: 140 }}>
      <header style={{
        background: ACCENT, color: "white", padding: "20px 16px", textAlign: "center",
      }}>
        <div style={{ fontSize: 14, opacity: 0.85, letterSpacing: 1, fontWeight: 600 }}>ZIGNAGENIX</div>
        <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700 }}>Sign Your Treatment Proposal</h1>
        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
          Review and acknowledge each document, then sign with your full legal name.
        </div>
      </header>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: 16 }}>

        {/* Per-document acknowledgment cards */}
        {documents.length === 0 ? (
          <div style={{
            background: "#FEE2E2", color: "#991B1B", padding: 16, borderRadius: 10,
            display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13,
          }}>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              No documents found on this proposal. Your physician needs to
              generate them before you can sign. Please contact your PEM
              at <a href="tel:+16197759646" style={{ color: ACCENT_DARK, fontWeight: 600 }}>+1-619-775-9646</a>.
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{
              fontSize: 14, fontWeight: 700, color: "#1F2937",
              margin: "0 0 10px 0",
            }}>
              Required documents ({documents.length})
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {documents.map(doc => {
                const acked = !!docAcknowledgments[doc.id];
                return (
                  <label
                    key={doc.id}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 12,
                      padding: 14, background: "white",
                      border: `2px solid ${acked ? "#62BB46" : "#E5E7EB"}`,
                      borderRadius: 10, cursor: "pointer", minHeight: 44,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={acked}
                      onChange={() => toggleAck(doc.id)}
                      style={{
                        marginTop: 3,
                        width: 20, height: 20, accentColor: "#62BB46",
                        cursor: "pointer", flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        I have read and understood: {doc.title || doc.document_category}
                      </div>
                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                        {doc.download_url ? (
                          <a
                            href={doc.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: ACCENT_DARK, textDecoration: "underline" }}
                          >
                            Open PDF in new tab
                          </a>
                        ) : "Document unavailable — reload the page"}
                      </div>
                    </div>
                    {acked && <Check size={20} color="#62BB46" style={{ flexShrink: 0, marginTop: 2 }} />}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* ESIGN Consent disclosure block — collapsible */}
        <div style={{
          background: "white", border: "1px solid #E5E7EB", borderRadius: 10,
          padding: 14, marginBottom: 16,
        }}>
          <button
            type="button"
            onClick={() => setShowEsignText(!showEsignText)}
            style={{
              background: "none", border: "none", padding: 0,
              fontSize: 12, color: ACCENT_DARK, fontWeight: 600,
              cursor: "pointer", textDecoration: "underline",
            }}
          >
            {showEsignText ? "Hide" : "Read"} the electronic-signature disclosure
          </button>
          {showEsignText && (
            <div style={{
              marginTop: 12, padding: 12, background: "#F9FAFB",
              borderRadius: 6, fontSize: 12, color: "#374151", lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}>
              {ESIGN_DISCLOSURE_TEXT}
            </div>
          )}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12,
            cursor: "pointer", minHeight: 44, paddingTop: 4,
          }}>
            <input
              type="checkbox"
              checked={esignConsent}
              onChange={(e) => setEsignConsent(e.target.checked)}
              style={{
                marginTop: 3,
                width: 20, height: 20, accentColor: "#62BB46",
                cursor: "pointer", flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 13, color: "#111827", lineHeight: 1.5 }}>
              I consent to use electronic signatures and electronic records
              for this proposal, and I understand my rights as described in
              the disclosure above.
            </span>
          </label>
        </div>

        {/* Typed name */}
        <div style={{
          background: "white", border: "1px solid #E5E7EB", borderRadius: 10,
          padding: 14, marginBottom: 16,
        }}>
          <label style={{
            fontSize: 13, fontWeight: 600, color: "#111827", display: "block",
            marginBottom: 6,
          }}>
            Type your full legal name as your signature
          </label>
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="e.g. Maria Rodriguez"
            autoComplete="name"
            style={{
              width: "100%",
              fontSize: 18,
              fontFamily: "'Brush Script MT', cursive, 'Apple Chancery'",
              padding: "10px 12px",
              border: `2px solid ${typedName.trim() ? ACCENT : "#E5E7EB"}`,
              borderRadius: 8, outline: "none",
              boxSizing: "border-box",
              minHeight: 44,
              color: "#111827",
            }}
          />
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
            Your typed name above constitutes your legal signature under NOM-151 (Mexico) and ESIGN (United States).
          </div>
        </div>

        {/* Inline error */}
        {errorMessage && (
          <div style={{
            background: "#FEE2E2", color: "#991B1B", padding: 12, borderRadius: 8,
            display: "flex", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 13,
          }}>
            <AlertTriangle size={16} /> {errorMessage}
          </div>
        )}
      </div>

      {/* Sticky sign button */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "white", borderTop: "1px solid #DEE2E6",
        padding: "12px 16px", boxShadow: "0 -4px 12px rgba(0,0,0,0.05)",
        zIndex: 100,
      }}>
        <button
          type="button"
          onClick={onSign}
          disabled={!canSign}
          style={{
            width: "100%", padding: "14px",
            background: canSign ? ACCENT : "#CBD5E1",
            color: "white", border: "none", borderRadius: 10,
            fontSize: 16, fontWeight: 700,
            cursor: canSign ? "pointer" : "not-allowed",
            minHeight: 48,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {signing
            ? (<><Loader2 size={18} className="spin" /> Recording your signature…</>)
            : (<><Check size={18} /> Sign and submit</>)
          }
        </button>
        {!canSign && !signing && (
          <div style={{ fontSize: 11, color: "#6B7280", textAlign: "center", marginTop: 8 }}>
            {documents.length === 0
              ? "No documents to sign"
              : !allAcknowledged ? "Acknowledge every document above"
              : !esignConsent ? "Tick the electronic signature consent"
              : !typedName.trim() ? "Type your full legal name"
              : "Ready to sign"}
          </div>
        )}
      </div>
    </div>
  );
}

// Brief in-line summary of the ESIGN disclosure. The full legally
// binding text is snapshotted server-side at signature time by
// routes/_e_signature_state.js buildConsentDisclosureText() — this is
// the UI-readable summary the patient sees before signing.
const ESIGN_DISCLOSURE_TEXT = `By proceeding, you consent to use electronic signatures and electronic records for this proposal. This consent is provided pursuant to the U.S. Electronic Signatures in Global and National Commerce Act (15 U.S.C. § 7001) and Mexico's NOM-151-SCFI-2016.

Your rights:
1. Paper copies — You may request a paper copy of any document signed electronically at any time, at no cost, by contacting ZignaGenix.
2. Withdraw consent — You may withdraw this consent at any time by written notice. Withdrawal does not affect documents already signed electronically.
3. Scope — This consent applies to all documents in this proposal AND any subsequent communications related to your care unless you specifically withdraw consent.
4. Hardware / software — You confirm you can read and retain electronic records in standard PDF format on a device with a modern web browser.

Contact for paper copies or consent withdrawal: +1-619-775-9646 or concierge@zignagenix.com

By typing your full legal name and submitting, you affirm:
(a) the typed name is your legal signature for this proposal;
(b) you have read and consented to each attached document;
(c) you have read and understood this electronic signature disclosure.`;

// ─── helpers ───────────────────────────────────────────────────────────────
function prettyModality(s) {
  if (!s) return "";
  return String(s).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function friendlyErrorFor(code) {
  const map = {
    REJECT_EXPIRED:    { title: "This link has expired", body: "Treatment proposal links are valid for 14 days from creation. Your link has reached its expiration date." },
    REJECT_REVOKED:    { title: "This link is no longer active", body: "This proposal link was deactivated by ZignaGenix. If you believe this is an error, please contact your Patient Experience Manager." },
    NOT_FOUND:         { title: "Link not found", body: "We couldn't find a proposal matching this link. The URL may be mistyped — try copying it from the original email or text message." },
    MALFORMED_TOKEN:   { title: "Link not recognized", body: "This link doesn't match the format we expect. Double-check that you copied the complete URL." },
    MISSING_TOKEN:     { title: "No link provided", body: "Open the proposal from the link your PEM emailed or texted you." },
    PROPOSAL_NOT_FOUND:{ title: "Proposal unavailable", body: "The proposal associated with this link is no longer available. Your PEM can generate a new one." },
    PROPOSAL_LOCKED:   { title: "Proposal already finalized", body: "This proposal has progressed past the selection stage. Your PEM will follow up on next steps." },
    NETWORK:           { title: "Connection problem", body: "We couldn't reach ZignaGenix. Check your internet connection and try again." },
  };
  return map[code] || {
    title: "Something went wrong",
    body: `An unexpected error occurred (${code || "unknown"}). Please contact your ZignaGenix Patient Experience Manager.`,
  };
}
